import { Create, Datagrid, Edit, List, Pagination, Resource, SaveButton, TextField } from "react-admin";
import { CloneIconButton } from "../buttons/CloneIconButton";
import { BackofficeListTitle } from "../misc/BackofficeListTitle";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { CreateRecordButton } from "../buttons/CreateRecordButton";
import { Form2 } from "../misc/Form2";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { DrivingLicenseClassLabel, ExamAuthorityFee } from "../providers/examAuthorityFeesProvider";
import { SelectInput } from "../inputs/SelectInput";
import { DrivingLicenseClassEnum } from "../model/DrivingLicenseClass";
import { MoneyInput } from "../inputs/MoneyInput";
import { DateInput } from "../inputs/DateInput";
import { MoneyField } from "../fields/MoneyField";
import { DateField } from "../fields/DateField";
import { grants } from "../backoffice.access_control";
import { formatMoney } from "../misc/Money";
import { SearchFilterInput } from "../inputs/SearchFilterInput";

const filters = [<SearchFilterInput key="q" source="q" alwaysOn resettable />];

function ExamAuthorityFeesList() {
  return (
    <List
      title={<BackofficeListTitle />}
      filters={filters}
      pagination={<Pagination />}
      sort={/* initial sort order: */ { field: "drivingLicenseClassLabel", order: "ASC" }}
      exporter={false}
      actions={grants.has("ExamAuthorityFee:create") && <CreateRecordButton />}
    >
      <Datagrid
        rowClick={grants.has("ExamAuthorityFee:edit") ? "edit" : false}
        bulkActionButtons={false}
        sx={{ "& td:last-child": { width: "1px" } }}
      >
        <TextField label="Führerscheinklasse" source="drivingLicenseClassLabel" />
        <MoneyField label="Theorieprüfung" source="theoryExamFee" />
        <MoneyField label="Praktische Prüfung" source="practicalExamFee" />
        <DateField label="Gültig ab" source="validFrom" />
        <DateField label="Gültig bis" source="validUntil" />
        <CloneIconButton />
      </Datagrid>
    </List>
  );
}

function ExamAuthorityFeeCreate() {
  return (
    <Create title={<BackofficeRecordTitle />}>
      <ExamAuthorityFeeForm mode="create" />
    </Create>
  );
}

function ExamAuthorityFeeEdit() {
  return (
    <Edit mutationMode="pessimistic" title={<BackofficeRecordTitle />}>
      <ExamAuthorityFeeForm mode="edit" />
    </Edit>
  );
}

function ExamAuthorityFeeForm({ mode: _mode }: { mode: "create" | "edit" }) {
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <_DrivingLicenseClassInput />
        <MoneyInput label="Theorieprüfung" source="theoryExamFee" size="small" sx={{ maxWidth: "300px" }} />
        <MoneyInput label="Praktische Prüfung" source="practicalExamFee" size="small" sx={{ maxWidth: "300px" }} />
        <DateInput
          label="Gültig ab"
          source="validFrom"
          size="small"
          sx={{ maxWidth: "300px" }}
          helperText={"\u00A0"}
          allowPastDates
        />
        <DateInput
          label="Gültig bis"
          source="validUntil"
          size="small"
          sx={{ maxWidth: "300px" }}
          helperText={"\u00A0"}
          required={false}
          allowPastDates
        />
        <Row>
          <SaveButton icon={<></>} alwaysEnable />
        </Row>
      </Column>
    </Form2>
  );
}

export const examAuthorityFeesResource = (
  <Resource
    key="examAuthorityFees"
    name="examAuthorityFees"
    list={ExamAuthorityFeesList}
    create={ExamAuthorityFeeCreate}
    edit={ExamAuthorityFeeEdit}
    options={{ label: "Prüfungsgebühren", createTitle: "Neue Prüfungsgebühr" }}
    recordRepresentation={_recordRepresentation}
  />
);

const _options: Array<[DrivingLicenseClassLabel, string]> = DrivingLicenseClassEnum.options.map((it) => [it, it]);
_options.push(["A Aufstieg", "A Aufstieg"]);
_options.push(["A2 Aufstieg", "A2 Aufstieg"]);
_options.sort((a, b) => a[0].localeCompare(b[0]));

function _DrivingLicenseClassInput() {
  return (
    <SelectInput
      source="drivingLicenseClassLabel"
      label="Führerscheinklasse"
      options={_options}
      validate={(value) => (value ? true : "Bitte wähle die Führerscheinklasse aus.")}
      required
      helperText={"\u00A0"}
      size="small"
      sx={{ maxWidth: "300px" }}
    />
  );
}

function _recordRepresentation(record: ExamAuthorityFee): string {
  let label = record.drivingLicenseClassLabel;
  if (record.theoryExamFee) {
    label += ` • Theorieprüfung: ${formatMoney(record.theoryExamFee)}`;
  }
  if (record.practicalExamFee) {
    label += ` • Praktische Prüfung: ${formatMoney(record.practicalExamFee)}`;
  }
  return label;
}
